import {
  createContext,
  FC,
  useContext,
  useMemo,
  KeyboardEvent,
  useEffect,
  useState,
  useRef
} from "react"
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import { Box } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { VariantsContainerComponent } from "../../ui/containers/VariantsContainer"
import {
  BasicDragContainerComponent,
  DragContainerComponent
} from "../../ui/containers/DragContainer"
import {
  generateAnnouncement,
  generateHumanReadableIds,
  getRestChildren,
  useDragDropMatch,
  useFindNodes as findNodes,
  shuffleArray
} from "../../helpers"
import {
  ContainerType,
  DropContainerScoring,
  DropContainerState,
  IClickMovingState,
  InteractionType,
  isQuestionComplete,
  NeutralSetting,
  onSaveStudentAnswer
} from "../../types"
import { DomNodes, FREE_VARIANTS_CONTAINER_ID } from "../../constants"
import { addUniqPartId, cutUniqPartId } from "../../helpers/utils"

interface GapMatchInteractionComponentProps {
  itemId: string
  shuffle: string
  minassociations: string
  maxassociations: string
  responseidentifier: string
  children: JSX.Element | JSX.Element[]
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  isCompletedQuestion?: isQuestionComplete
  isNeutralSetting?: NeutralSetting
  containerType?: ContainerType
}

interface IGapContext {
  fluidGaps: boolean
  containers: DropContainerState
  scoredContainers: DropContainerScoring
  variants: JSX.Element[]
  isFinished: boolean
  isMaxMatches: boolean
  handleRemove: (itemId: string, containerId: string) => () => void
  clickMovingState: IClickMovingState
  containersArray: string[]
  variantsArray: string[]
  groupMode: boolean
  groupStatistics?: Record<string, number>
}

const GapContext = createContext<IGapContext>({
  fluidGaps: false,
  containers: {},
  scoredContainers: {},
  variants: [],
  isFinished: false,
  isMaxMatches: false,
  handleRemove: (itemId: string, containerId: string) => () => {},
  clickMovingState: {
    activeGapId: null,
    activeGapBlockId: null,
    activeBlockId: null
  },
  containersArray: [],
  variantsArray: [],
  groupMode: false
})

const transformToResponse = (value: any) => {
  return value
    ? Object.entries(value)
        .filter(item => item[1])
        .map(item => `${item[0]} ${item[1]}`)
    : []
}

export const GapMatchInteractionComponent: FC<
  GapMatchInteractionComponentProps
> = props => {
  const {
    shuffle,
    minassociations: minAssociations = "0",
    maxassociations: maxAssociations = "0",
    responseidentifier: responseIdentifier,
    children,
    accessibilityAttr,
    onSaveStudentAnswer,
    isCompletedQuestion,
    isNeutralSetting,
    containerType
  } = props

  const variantsRef = useRef(null)
  const [[prompt, gapText, gap], restNodes] = useMemo(
    () => [
      [
        ...findNodes(children, [
          DomNodes.prompt,
          DomNodes.gapText,
          DomNodes.gap
        ])
      ],
      getRestChildren(children, [
        DomNodes.prompt,
        DomNodes.gapText,
        DomNodes.gapImg
      ])
    ],
    [children]
  )

  // Shuffle variants only once on initial load; prevent re-shuffling on every render
  if (!variantsRef.current) {
    variantsRef.current = shuffleArray(shuffle === "true", gapText)
  }

  const variants = variantsRef.current as JSX.Element[]

  const dropContainers = useMemo(
    () => gap.map(g => g.props.identifier as string),
    [gap]
  )

  const activationConstraint = {
    distance: 15
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint
    })
  )

  const { questionCompleted, correctAnswers } = isCompletedQuestion?.() ?? {
    questionCompleted: false,
    correctAnswers: []
  }

  const {
    activeId,
    containers,
    scoredContainers,
    isFinished,
    isMaxMatches,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleRemove,
    isDisabledVariant,
    clickMovingState,
    resetDrag,
    groupMode,
    groupStatistics
  } = useDragDropMatch(
    responseIdentifier,
    variants,
    dropContainers,
    Number(minAssociations),
    Number(maxAssociations),
    InteractionType.gapMatch,
    transformToResponse,
    undefined, // responseToContainers
    containerType
  )

  // Track previous state to detect actual placements
  const [prevClickMovingState, setPrevClickMovingState] =
    useState(clickMovingState)

  useEffect(() => {
    const { activeGapId, activeGapBlockId, swappedBlockId, swappedGapId } =
      clickMovingState
    const { activeGapId: prevGapId, activeGapBlockId: prevBlockId } =
      prevClickMovingState

    // Only trigger if we have a new placement (both values set and different from previous)
    if (
      !!activeGapId &&
      !!activeGapBlockId &&
      (activeGapId !== prevGapId || activeGapBlockId !== prevBlockId)
    ) {
      onSaveStudentAnswer?.({
        interactionType: InteractionType.gapMatch,
        answerId: `${activeGapBlockId} ${activeGapId}`,
        minChoices: Number(minAssociations),
        maxChoices: Number(maxAssociations)
      })

      // If there was a swap, also save the swapped item
      if (swappedBlockId && swappedGapId) {
        onSaveStudentAnswer?.({
          interactionType: InteractionType.gapMatch,
          answerId: `${swappedBlockId} ${swappedGapId}`,
          minChoices: Number(minAssociations),
          maxChoices: Number(maxAssociations)
        })
      }
    }

    setPrevClickMovingState(clickMovingState)
  }, [
    clickMovingState,
    minAssociations,
    maxAssociations,
    onSaveStudentAnswer,
    prevClickMovingState
  ])

  const contianerAnswers = useMemo(() => {
    if (questionCompleted && !!correctAnswers && !isNeutralSetting) {
      const correctContainers: Record<string, string> = correctAnswers.reduce(
        (acc, pair) => {
          const [variant, contianer] = pair.split(" ")
          if (variant && contianer) {
            acc[contianer] = variant
          }
          return acc
        },
        {} as Record<string, string>
      )
      return correctContainers
    }
    return containers
  }, [questionCompleted, correctAnswers, containers, isNeutralSetting])

  const gapContextValue = useMemo(
    () => ({
      fluidGaps: true,
      containers: contianerAnswers,
      scoredContainers,
      variants,
      isFinished,
      isMaxMatches,
      handleRemove,
      clickMovingState,
      containersArray: dropContainers,
      variantsArray: Object.values(variants).map(item => item.props.identifier),
      groupMode,
      groupStatistics
    }),
    [
      contianerAnswers,
      scoredContainers,
      variants,
      isFinished,
      isMaxMatches,
      handleRemove,
      clickMovingState,
      dropContainers,
      groupMode,
      groupStatistics
    ]
  )

  const gapNaming: Array<[string, string]> = useMemo(
    () => [
      [FREE_VARIANTS_CONTAINER_ID, "Answer Choices Container"],
      ...dropContainers.map(
        (id, index) => [id, `Container ${index + 1}`] as [string, string]
      )
    ],
    [dropContainers]
  )

  const announcements = generateAnnouncement(
    generateHumanReadableIds(variants),
    generateHumanReadableIds(gap),
    false,
    gapNaming
  )

  const dragContainerKeyDownHandler = (
    event: KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === "Escape") {
      resetDrag()
      return
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      ;(event.target as HTMLElement).click()
    }
  }

  const humanReadableVariants = useMemo(
    () => generateHumanReadableIds(variants),
    [variants]
  )

  const prevClickMovingRef = useRef<IClickMovingState>(clickMovingState)
  const [statusMessage, setStatusMessage] = useState("")

  useEffect(() => {
    const { activeBlockId, activeGapId, activeGapBlockId } = clickMovingState
    const prev = prevClickMovingRef.current
    const name = (id: string) =>
      (humanReadableVariants && humanReadableVariants[id]) || id

    let message = ""
    if (activeGapId && activeGapBlockId) {
      const gapIndex = dropContainers.indexOf(activeGapId)
      message = `${name(activeGapBlockId)} placed in drop area ${
        gapIndex !== -1 ? gapIndex + 1 : ""
      }.`
    } else if (activeBlockId && !activeGapId) {
      message = `${name(
        activeBlockId
      )} selected. Tab to a drop area and press Space to place it.`
    } else if (!activeBlockId && !activeGapBlockId) {
      if (prev.activeBlockId || prev.activeGapBlockId) {
        message = "Selection cancelled."
      }
    }

    prevClickMovingRef.current = clickMovingState
    setStatusMessage(message)
  }, [clickMovingState, humanReadableVariants, dropContainers])

  return (
    <div {...accessibilityAttr}>
      <div aria-live="assertive" aria-atomic="true" style={visuallyHidden}>
        {statusMessage}
      </div>
      {prompt}
      <GapContext.Provider value={gapContextValue}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={(e: DragEndEvent) => handleDragEnd(e)}
          onDragOver={handleDragOver}
          accessibility={{
            announcements,
            screenReaderInstructions: {
              draggable: "Space to select, Tab to a drop area, Space to place."
            }
          }}
        >
          <VariantsContainerComponent>
            {variants.map((variant, idx) => {
              const id = variant.props.identifier

              return (
                <Box onKeyDown={dragContainerKeyDownHandler}>
                  <DragContainerComponent
                    id={addUniqPartId(id)}
                    containerId={FREE_VARIANTS_CONTAINER_ID}
                    drag={id === cutUniqPartId(activeId)}
                    disabled={isDisabledVariant(id) || questionCompleted}
                    selected={
                      clickMovingState.activeBlockId === id &&
                      !clickMovingState.activeGapId
                    }
                    variantIndex={idx}
                    fluid
                  >
                    {variant.props.children}
                  </DragContainerComponent>
                </Box>
              )
            })}
          </VariantsContainerComponent>
          {restNodes}
          <DragOverlay dropAnimation={null}>
            {activeId && (
              <BasicDragContainerComponent placehold fluid>
                <>
                  {variants.find(
                    v => v.props.identifier === cutUniqPartId(activeId)
                  )}
                </>
              </BasicDragContainerComponent>
            )}
          </DragOverlay>
        </DndContext>
      </GapContext.Provider>
    </div>
  )
}

export const useGapContextValue = () => useContext(GapContext)
